<template>
  <owal-modal-panel
    :top-buttons="close"
    presentation-style="form"
    title="Event Metrics"
    @button-click="callback()"
  >
    <div class="owal-event-metrics-card__container">
      <div class="owal-event-metrics-card__small">
        <line-chart
          :chart-data="peakTimes"
          :options="peakActivityOptions"
        />
      </div>
      <div class="owal-event-metrics-card__small">
        <bar-chart
          :chart-data="detectionCounts"
          :options="detectionCountOptions"
        />
      </div>
      <div>
        <bar-chart
          :width="800"
          :height="500"
          :chart-data="mostActiveCameras"
          :options="mostActiveOptions"
        />
      </div>
    </div>
  </owal-modal-panel>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { parseEventsByDay } from '@/libraries/util/events'
import moment from 'moment'
import { LineChart, BarChart } from '@/libraries/chart/createChart.js'
import OwalModalPanel from '@/libraries/owal-ui/panels/OwalModalPanel'

export default {
  name: 'OwalEventsMetricsCard',
  components: { LineChart, BarChart, OwalModalPanel },
  props: ['date'],
  data: function () {
    return {
      camera: undefined,
      detectionTypes: null,
      detectionTypeDescription: undefined,
      detectionTypeDetection: undefined,
      peakActivityOptions: {
        mainAspectRatio: false,
        responsive: false,
        title: {
          display: true,
          text: `${this.date}`,
          fontStyle: 'bold',
        },
        tooltips: {
          titleFontSize: 14,
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              parser: 'ha',
              unit: 'hour',
              unitStepSize: 1,
            },
            display: true,
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              userCallback: function (label, index, labels) {
                // when the floored value is the same as the value we have a whole number
                if (Math.floor(label) === label) {
                  return label
                }
              },
            },
            display: true,
          }],
        },
      },
      detectionCountOptions: {
        responsive: false,
        mainAspectRatio: false,
        tooltips: {
          titleFontSize: 14,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              userCallback: function (label, index, labels) {
                // when the floored value is the same as the value we have a whole number
                if (Math.floor(label) === label) {
                  return label
                }
              },
            },
          }],
          xAxes: [{
            ticks: {
              autoskip: false,
            },
          }],
        },
      },
      mostActiveOptions: {
        responsive: false,
        mainAspectRatio: false,
        title: {
          display: true,
          text: 'Most Active Areas',
        },
        tooltips: {
          titleFontSize: 14,
          titleSpacing: 6,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              userCallback: function (label, index, labels) {
                // when the floored value is the same as the value we have a whole number
                if (Math.floor(label) === label) {
                  return label
                }
              },
            },
          }],
          xAxes: [{
            ticks: {
              autoskip: false,
              stepSize: 1,
              fontSize: 9,
            },
          }],
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      events: 'events/events',
      filters: 'events/filters',
      byDay: 'events/eventsByDay',
    }),
    mostActiveCameras () {
      const eventsByDay = parseEventsByDay(this.events)
      const events = eventsByDay[this.date]

      let camerasDict = {}

      events.map(event => {
        if (event.cameraDescription in camerasDict) {
          camerasDict[event.cameraDescription] += 1
        } else {
          camerasDict[event.cameraDescription] = 1
        }
      })

      let sortable = []

      const camerasByCount = {}
      for (let camera in camerasDict) {
        if (!camerasByCount[camerasDict[camera]]) {
          camerasByCount[camerasDict[camera]] = []
        }
        camerasByCount[camerasDict[camera]].push(camera)
      }

      for (let events in camerasByCount) {
        sortable.push([events, camerasByCount[events]])
      }

      sortable.sort((a, b) => {
        return b[0] - a[0]
      })

      let cameraDescriptions = []
      let eventsCount = []
      let colors = []

      sortable.map(result => {
        cameraDescriptions.push(result[1])
        eventsCount.push(result[0])
        colors.push(this.dynamicColors())
      })
      let dataCollection = {
        labels: cameraDescriptions,
        datasets: [
          {
            label: 'Events Per Camera',
            backgroundColor: colors,
            data: eventsCount,
          },
        ],
      }
      return dataCollection
    },
    detectionCounts () {
      const eventsByDay = parseEventsByDay(this.events)
      const events = eventsByDay[this.date]
      const detectionsDict = {}
      events.forEach(event => {
        event.metrics.forEach(detectable => {
          const description = Object.keys(detectable)[0]

          if (description in detectionsDict) {
            detectionsDict[description] += detectable[description]
          } else {
            detectionsDict[description] = detectable[description]
          }
        })
      })

      let sortable = []

      for (var metric in detectionsDict) {
        sortable.push([metric, detectionsDict[metric]])
      }

      sortable.sort((a, b) => {
        return b[1] - a[1]
      })
      const detections = []
      sortable.map(result => {
        if (result[1] !== 0) {
          detections.push({
            description: result[0].replace('ssd_', ''),
            detections: result[1],
          })
        }
      })
      const detectionTypeDescription = detections.map(detection => {
        return detection.description
      })

      const detectionTypeDetection = detections.map(detection => {
        return detection.detections
      })

      let dataCollection = {
        labels: detectionTypeDescription,
        datasets: [
          {
            label: 'Detection Count',
            backgroundColor: ['rgba(71, 183,132,.5)', 'rgba(201, 43, 12,.5)'],
            data: detectionTypeDetection,
          },
        ],
      }

      return dataCollection
    },
    peakTimes () {
      const eventsByDay = parseEventsByDay(this.events)
      const events = eventsByDay[this.date]
      let hoursDict = {}

      let startOfDay = moment().startOf('day')

      // if it's the current date, show peak times up until the current hour (can't predict the future yet ;) )
      if (moment(this.date).isSame(moment().format('dddd, MMMM D YYYY'))) {
        while (startOfDay.isBefore()) {
          hoursDict[startOfDay.format('ha')] = 0
          startOfDay = startOfDay.add(1, 'hours')
        }
      } else {
        // otherwise show peak times throughout the whole day
        let endOfDay = moment().endOf('day')

        while (startOfDay.isBefore(endOfDay)) {
          hoursDict[startOfDay.format('ha')] = 0
          startOfDay = startOfDay.add(1, 'hours')
        }
      }

      events.map(event => {
        const hour = moment(event.timestamp).format('ha')
        if (hour in hoursDict) {
          hoursDict[hour] += 1
        }
      })

      let time = []
      let eventsCount = []

      for (var hour in hoursDict) {
        time.push(hour)
        eventsCount.push(hoursDict[hour])
      }

      let dataCollection = {
        labels: time,
        datasets: [
          {
            label: `Events Per Hour`,
            backgroundColor: [
              this.dynamicColors(),
            ],
            data: eventsCount,
          },
        ],
      }
      return dataCollection
    },
    close  () {
      const buttons = [{
        title: 'Close',
        identifier: 'close',
      }]
      return buttons
    },
  },
  methods: {
    ...mapMutations({
      updateFilters: 'events/updateFilters',
    }),
    toggleFilter (filter) {
      if (!this.filters.includes(filter)) {
        this.updateFilters({ add: true, filter })
      } else {
        this.updateFilters({ add: false, filter })
      }
    },
    filterToggleClass (description) {
      const currentFilters = this.filters.cameras.concat(this.filters.detectables)
      return `filter-toggle ${currentFilters.includes(description) ? 'active' : ''}`
    },
    dynamicColors () {
      const r = Math.floor(Math.random() * 255)
      const g = Math.floor(Math.random() * 255)
      const b = Math.floor(Math.random() * 255)
      return `rgb(${r}, ${g}, ${b}, 0.5)`
    },
    callback () {
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
  .owal-event-metrics-card__container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    background: white;
    z-index: 700;
  }

  .owal-event-metrics-card__container>* {
      flex: 1 1 100px;
  }

  .owal-event-metrics-card__small {
    max-width: 100%;
  }
</style>
